<template>
  <div class="signature_iframe_container">
    <iframe
      ref="signature_iframe"
      sandbox="allow-same-origin"
      class="signature_iframe"
      :style="{ height: `${height}px` }"
    ></iframe>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick, computed } from "vue";
import store from "@/src/store";

const props = defineProps({
  content: {
    type: String,
    required: true,
  },
  height: {
    type: Number,
    default: 300,
  },
});

const signature_iframe = ref(null);

const effective_theme = computed(
  () => store.getters["theme/getEffectiveTheme"],
);
const is_dark_theme = computed(() => effective_theme.value === "DARK");

const inject_styles = (iframe_doc) => {
  if (!iframe_doc || !iframe_doc.head) return;
  const text_color = is_dark_theme.value ? "#ffffff" : "#000000";

  const style = document.createElement("style");
  style.textContent = `
    * {
      box-sizing: border-box;
    }
    
    body {
      font-family: "Proxima Nova", Arial, sans-serif;
      margin: 0;
      padding: 16px;
      background: transparent;
      color: ${text_color};
      font-size: 14px;
      line-height: 1.4;
    }
    
        /* Theme-aware default text colors for elements without explicit color styling */
    p:not([style*="color"]) {
      color: ${text_color} !important;
    }
    
    p:not([style*="margin"]) {
      margin: 0px;
    }
    
    span:not([style*="color"]) {
      color: ${text_color} !important;
    }
    
    div:not([style*="color"]) {
      color: ${text_color} !important;
    }
    
    p[style]:not([style*="color"]) {
      color: ${text_color} !important;
    }
    
    span[style]:not([style*="color"]) {
      color: ${text_color} !important;
    }
    
    div[style]:not([style*="color"]) {
      color: ${text_color} !important;
    }
    
    /* Preserve all signature styling - no overrides for styled elements */
    table {
      border-collapse: collapse;
    }
    
    img {
      max-width: 100%;
      height: auto;
    }
    
    a {
      text-decoration: none;
    }
    
    /* Ensure proper display for elements with ng-scope classes */
    .ng-scope,
    .ng-binding {
      display: inherit;
    }
    
    table.ng-scope {
      display: table;
    }
    
    tr.ng-scope {
      display: table-row;
    }
    
    td.ng-scope {
      display: table-cell;
    }
    
    span.ng-scope {
      display: inline;
    }
    
    ol {
      list-style-type: decimal;
      padding-left: 2.5rem;
    }
    
    ul {
      list-style-type: disc;
      padding-left: 2.5rem;
    }
    
    ol li[data-list="bullet"] {
      list-style-type: disc !important;
    }
    
    ol li[data-list="bullet"]::marker {
      content: "• " !important;
    }
  `;

  iframe_doc.head.appendChild(style);
};

const update_iframe_content = () => {
  if (!signature_iframe.value || !props.content) return;

  const iframe = signature_iframe.value;
  const iframe_doc = iframe.contentDocument || iframe.contentWindow?.document;

  if (!iframe_doc) return;

  iframe_doc.open();
  iframe_doc.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
    </head>
    <body>
      ${props.content}
    </body>
    </html>
  `);
  iframe_doc.close();

  nextTick(() => {
    inject_styles(iframe_doc);
    update_iframe_height();
  });
};

const update_iframe_height = () => {
  if (!signature_iframe.value) return;

  setTimeout(() => {
    const iframe = signature_iframe.value;
    const iframe_doc = iframe.contentDocument || iframe.contentWindow?.document;

    if (!iframe_doc?.body) return;

    const body = iframe_doc.body;
    const scroll_height = body.scrollHeight;
    const offset_height = body.offsetHeight;
    const content_height = Math.max(scroll_height, offset_height);

    const final_height = Math.min(content_height + 32, props.height);
    iframe.style.height = `${final_height}px`;
  }, 100);
};

watch(
  [() => props.content, () => effective_theme.value],
  () => {
    update_iframe_content();
  },
  { immediate: true },
);

onMounted(() => {
  update_iframe_content();
});
</script>

<style scoped>
.signature_iframe_container {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
}

.signature_iframe {
  width: 100%;
  border: none;
  background: transparent;
  display: block;
  transition: height 0.3s ease;
}
</style>
